import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, ProductSubscriptionContractDetail } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-subscription-contract-detail',
  imports: [
    AinPageComponent, AinStateComponent, TableModule, ButtonModule, RouterLink, DatePipe,
    FormsModule, CheckboxModule
  ],
  template: `
    <ain-page [title]="t('catalog.subscription.contractDetail.title')" [description]="t('catalog.subscription.contractDetail.description')">
      <p class="back"><a routerLink="/subscription/contracts">{{ t('catalog.subscription.contracts.title') }}</a></p>
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!detail()) {
        <ain-state type="empty" [title]="t('catalog.subscription.contractDetail.notFound')" />
      } @else {
        <section class="panel">
          <div class="summary">
            <span>{{ t('catalog.tracking.status') }}: <strong>{{ detail()!.contract.status }}</strong></span>
            <span>{{ t('catalog.subscription.contractDetail.customer') }}: {{ detail()!.contract.customerReference || '—' }}</span>
            <span>{{ t('catalog.subscription.contractDetail.periodEnd') }}: {{ detail()!.contract.currentPeriodEndUtc | date:'medium' }}</span>
          </div>
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.subscription.billing.runDue')" [disabled]="acting()" (click)="runDueBilling()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.subscription.billing.processLifecycle')" [disabled]="acting()" (click)="processLifecycle()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" [disabled]="acting()" (click)="load()"></button>
          </div>
          <div class="check-row">
            <p-checkbox [(ngModel)]="postFinancial" [binary]="true" inputId="postSubFin" />
            <label for="postSubFin">{{ t('catalog.digital.entitlements.postFinancial') }}</label>
          </div>
          @if (message()) { <p class="message">{{ message() }}</p> }

          <h3>{{ t('catalog.subscription.invoices.title') }}</h3>
          <p-table [value]="detail()!.invoices">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.subscription.invoices.number') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.subscription.invoices.due') }}</th>
                <th>{{ t('catalog.subscription.invoices.amount') }}</th>
                <th>{{ t('catalog.subscription.invoices.recognizedAmount') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.invoiceNumber || '—' }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.dueAtUtc | date:'medium' }}</td>
                <td>{{ row.amount }} {{ row.currencyCode }}</td>
                <td>{{ row.recognizedAmount }} {{ row.isRecognized ? '✓' : '' }}</td>
                <td class="actions">
                  @if (row.status === 'Scheduled') {
                    <button pButton type="button" size="small" [label]="t('catalog.subscription.invoices.issue')" [disabled]="acting()" (click)="issueInvoice(row.id)"></button>
                  }
                  @if (row.status === 'Issued') {
                    <button pButton type="button" size="small" [label]="t('catalog.subscription.invoices.recognize')" [disabled]="acting()" (click)="recognizeInvoice(row.id)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>

          <h3>{{ t('catalog.subscription.lifecycle.title') }}</h3>
          <p-table [value]="detail()!.lifecycleEvents">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.regFinance.cockpit.notes') }}</th>
                <th>{{ t('catalog.digital.licenseCenter.created') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.eventType }}</td>
                <td>{{ row.notes }}</td>
                <td>{{ row.createdAtUtc | date:'medium' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .back { margin: 0 0 8px; }
    .panel { display: grid; gap: 12px; }
    .summary { display: flex; flex-wrap: wrap; gap: 16px; font-size: 0.9rem; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .check-row { display: flex; align-items: center; gap: 8px; }
    .message { margin: 0; color: #166534; font-weight: 600; }
    h3 { margin: 0; font-size: 1rem; }
    .actions { display: flex; gap: 6px; }
  `]
})
export class SubscriptionContractDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly detail = signal<ProductSubscriptionContractDetail | null>(null);
  readonly message = signal('');

  postFinancial = true;

  ngOnInit(): void {
    this.route.paramMap.subscribe(() => this.load());
  }

  load(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) { this.loading.set(false); return; }
    this.loading.set(true);
    this.message.set('');
    this.service.getProductSubscriptionContractDetail(id).subscribe({
      next: d => { this.detail.set(d); this.loading.set(false); },
      error: () => { this.detail.set(null); this.loading.set(false); }
    });
  }

  runDueBilling(): void {
    this.acting.set(true);
    this.service.runProductSubscriptionDueBilling(this.postFinancial).subscribe({
      next: r => {
        this.message.set(this.t('catalog.subscription.billing.runDueResult').replace('{count}', String(r.issuedCount)));
        this.acting.set(false);
        this.load();
      },
      error: () => this.acting.set(false)
    });
  }

  processLifecycle(): void {
    this.acting.set(true);
    this.service.processProductSubscriptionLifecycle(this.postFinancial).subscribe({
      next: r => {
        this.message.set(
          this.t('catalog.subscription.billing.lifecycleResult')
            .replace('{grace}', String(r.enteredGraceCount))
            .replace('{expired}', String(r.expiredCount))
            .replace('{issued}', String(r.issuedCount)));
        this.acting.set(false);
        this.load();
      },
      error: () => this.acting.set(false)
    });
  }

  issueInvoice(scheduleId: string): void {
    this.acting.set(true);
    this.service.issueProductSubscriptionInvoice(scheduleId, this.postFinancial).subscribe({
      next: () => {
        this.message.set(this.t('catalog.subscription.invoices.issuedSuccess'));
        this.acting.set(false);
        this.load();
      },
      error: () => this.acting.set(false)
    });
  }

  recognizeInvoice(scheduleId: string): void {
    this.acting.set(true);
    this.service.recognizeProductSubscriptionRevenue(scheduleId, this.postFinancial).subscribe({
      next: () => {
        this.message.set(this.t('catalog.subscription.invoices.recognizedSuccess'));
        this.acting.set(false);
        this.load();
      },
      error: () => this.acting.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
